import React, { useState } from 'react';
import _ from 'lodash';
import cn from 'classnames';
import cogoToast from 'cogo-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/pro-light-svg-icons';
import { spotlights } from '@shopmy/universal';

import { featureFlagLabel } from './BrandFeatureFlagsModal';
import { setBrandFeatureFlagsBulk } from '../../api/spotlights';
import './BrandFeatureFlags.scss';

const { SPOTLIGHT_BRAND_FEATURE_FLAGS, getSpotlightBrandFeatureFlagPrerequisites, getSpotlightBrandFeatureFlagDependents } = spotlights;

const MAX_BRANDS = 500;
const CHOICES = [
  { key: 'skip', label: 'No change' },
  { key: 'on', label: 'On', value: true },
  { key: 'off', label: 'Off', value: false },
  { key: 'clear', label: 'Clear', value: null }
];
const choiceFor = key => CHOICES.find(choice => choice.key === key);
const valueChoiceKey = value => (value === null ? 'clear' : value ? 'on' : 'off');

const BrandFeatureFlagsBulkModal = ({ presetBrandIds = [], onClose, onDone }) => {
  const [brandIdsText, setBrandIdsText] = useState(presetBrandIds.join(', '));
  const [choices, setChoices] = useState({});
  const [step, setStep] = useState('edit'); // 'edit' | 'confirm' | 'done'
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  const idTokens = brandIdsText.split(/[\s,]+/).filter(Boolean);
  const invalidIds = idTokens.filter(token => !/^\d+$/.test(token) || Number(token) === 0);
  const brandIds = _.uniq(idTokens.filter(token => !invalidIds.includes(token)).map(Number));
  const flags = _.mapValues(
    _.pickBy(choices, choice => choice !== 'skip'),
    choice => choiceFor(choice).value
  );
  const flagCount = Object.keys(flags).length;
  const tooMany = brandIds.length > MAX_BRANDS;
  const canReview = brandIds.length > 0 && flagCount > 0 && !tooMany && !invalidIds.length;

  const choose = (key, choice) =>
    setChoices(prev => {
      const next = { ...prev, [key]: choice };
      if (choice === 'on') getSpotlightBrandFeatureFlagPrerequisites(key).forEach(prerequisite => (next[prerequisite] = 'on'));
      return next;
    });

  const submit = async () => {
    setSaving(true);
    try {
      const r = await setBrandFeatureFlagsBulk(brandIds, flags);
      setResult(r);
      setStep('done');
      onDone && onDone();
    } catch (e) {
      cogoToast.error(typeof e === 'string' ? e : 'Failed to update feature flags.');
    } finally {
      setSaving(false);
    }
  };

  const skipped = (result?.results || []).filter(r => r.status === 'no_settings').map(r => r.brandId);

  return (
    <div className='sl-modal-overlay' onMouseDown={() => !saving && onClose()}>
      <div className='sl-modal' onMouseDown={e => e.stopPropagation()}>
        <div className='sl-modal-head'>
          <div className='sl-modal-title'>Set feature flags</div>
          <button className='sl-modal-close' disabled={saving} onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className='sl-modal-body'>
          {step === 'edit' && (
            <>
              <div className='sl-card-label' style={{ marginBottom: 8 }}>
                Brand IDs (comma-separated)
              </div>
              <textarea
                className='sl-vip-textarea'
                placeholder='e.g. 123, 456, 789'
                value={brandIdsText}
                onChange={e => setBrandIdsText(e.target.value)}
              />
              {tooMany && <div className='sl-flag-warning'>Set flags for at most {MAX_BRANDS} brands at a time.</div>}
              {invalidIds.length > 0 && <div className='sl-flag-warning'>Not valid brand IDs: {invalidIds.join(', ')}</div>}

              <div className='sl-flag-list' style={{ marginTop: 14 }}>
                {SPOTLIGHT_BRAND_FEATURE_FLAGS.map(flag => {
                  const choice = choices[flag.key] || 'skip';
                  const turnsOff = choice === 'off' || choice === 'clear';
                  const stranded = turnsOff
                    ? getSpotlightBrandFeatureFlagDependents(flag.key).filter(dependent => !['off', 'clear'].includes(choices[dependent]))
                    : [];
                  return (
                    <div className='sl-flag-row stacked' key={flag.key}>
                      <div className='sl-flag-info'>
                        <span className='label'>{flag.label}</span>
                        <span className='description'>{flag.description}</span>
                        {flag.requires.length > 0 && <span className='requires'>Needs {flag.requires.map(featureFlagLabel).join(' + ')}</span>}
                        {stranded.length > 0 && (
                          <span className='warning'>{stranded.map(featureFlagLabel).join(', ')} stop working on brands that have them</span>
                        )}
                      </div>
                      <div className='sl-segmented'>
                        {CHOICES.map(c => (
                          <div key={c.key} className={cn('sl-segment', { active: choice === c.key })} onClick={() => choose(flag.key, c.key)}>
                            {c.label}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className='sl-flag-footer'>
                <button className='sl-btn secondary' onClick={onClose}>
                  Cancel
                </button>
                <button className='sl-btn' disabled={!canReview} onClick={() => setStep('confirm')}>
                  Review
                </button>
              </div>
            </>
          )}

          {step === 'confirm' && (
            <>
              <div className='sl-card-sub' style={{ marginBottom: 12 }}>
                Apply to {brandIds.length} brand{brandIds.length === 1 ? '' : 's'}. Flags not listed stay as they are.
              </div>
              <div className='sl-flag-list'>
                {Object.entries(flags).map(([key, value]) => (
                  <div className='sl-flag-row' key={key}>
                    <span className='label'>{featureFlagLabel(key)}</span>
                    <span className={cn('sl-pill', { green: value === true, red: value === false })}>{choiceFor(valueChoiceKey(value)).label}</span>
                  </div>
                ))}
              </div>
              <div className='sl-flag-footer'>
                <button className='sl-btn secondary' disabled={saving} onClick={() => setStep('edit')}>
                  Back
                </button>
                <button className='sl-btn' disabled={saving} onClick={submit}>
                  {saving ? 'Working…' : `Set flags on ${brandIds.length} brand${brandIds.length === 1 ? '' : 's'}`}
                </button>
              </div>
            </>
          )}

          {step === 'done' && result && (
            <>
              <div className='sl-card-sub'>
                Updated <strong>{result.updated}</strong> of {brandIds.length} brand{brandIds.length === 1 ? '' : 's'}.
              </div>
              {skipped.length > 0 && <div className='sl-flag-warning'>No BrandSettings row, skipped: {skipped.join(', ')}</div>}
              <div className='sl-flag-footer'>
                <button className='sl-btn' onClick={onClose}>
                  Done
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BrandFeatureFlagsBulkModal;
